// Starter save. MVP begins with ~2-week-old chicks in a starter brooder
// (MASTER-PLAN.md §2.16): the arc is grow-out → pullet → laying hen.

import { CURRENT_SAVE_VERSION } from './persistence';
import { DEFAULT_SPEED } from '../sim/time';
import { Rng } from './rng';
import { assignPersonality } from '../sim/access';
import { genotypeForBreed } from '../sim/genetics';
import { getItem, initialStructureState } from '../sim/items';
import { createPhysiology } from '../sim/physiology';
import { seasonStartEggits } from '../sim/season';
import { ensureSpots } from '../sim/spots';
import type {
  Breed,
  ChickenEntity,
  GameState,
  GridState,
  HousingType,
  Structure,
  TerrainType,
  WeatherState,
} from './types';

/**
 * v2 adds physiology, genotypes, structures, weather and progression.
 * v3 adds the certificate-tracking counters the education layer needs.
 */
export const SCHEMA_VERSION = 3;

const STARTER_BREEDS: Breed[] = ['silkie', 'leghorn', 'rhode_island_red'];
const STARTER_NAMES = ['Pollita', 'Nube', 'Canela'];
// One starter rooster so a new player can breed without first grinding the shop
// (was 3 hens only; breeding required a rooster). Also keeps day-one gameplay
// from dead-ending at the nest box.
const STARTER_ROOSTER_BREED: Breed = 'rhode_island_red';
const STARTER_ROOSTER_NAME = 'Filo';

export { STARTER_ROOSTER_BREED, STARTER_ROOSTER_NAME };

export function createGrid(
  width: number,
  height: number,
  terrainType: TerrainType = 'grass',
): GridState {
  const tiles = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      tiles.push({ x, y, terrainType, dirtinessLevel: 0 });
    }
  }
  return { width, height, tiles };
}

export function newId(prefix: string, rng: Rng): string {
  return `${prefix}_${rng.int(0, 0xffffff).toString(16).padStart(6, '0')}`;
}

/**
 * Builds a bird, and draws everything about her that the game keeps to itself.
 *
 * `joinedOnDay` is a parameter rather than something a caller stamps on
 * afterwards because the reveal of her three hidden numbers is dated from it
 * (REFOCUS §8: you learn a bird by watching her). Drawing the personality
 * against a day the caller has not written yet is how a bird bought on day 60
 * used to arrive already revealed, so the two are written here from the same
 * value, once. Default 0 is the starter save, which begins on day 0.
 */
export function createChick(
  rng: Rng,
  breed: Breed,
  name: string,
  position: { x: number; y: number },
  ageInDays = 14,
  gender: 'rooster' | 'hen' = 'hen',
  joinedOnDay = 0,
): ChickenEntity {
  const chicken: ChickenEntity = {
    id: newId('chk', rng),
    name,
    breed,
    stage: 'chick',
    ageInDays,
    gender,
    physiology: createPhysiology(),
    genotype: genotypeForBreed(breed, rng),
    position: { x: position.x, y: position.y, tileZ: 0 },
    currentState: 'IDLE',
    conditions: [],
    eggs: {
      // Personal offset so the flock does not lay in lockstep.
      ovulationOffsetHours: rng.range(0, 4),
      nextLayAtHour: Infinity,
      totalLaid: 0,
      eggEatingHabit: false,
      promptCollectionDays: 0,
    },
    hierarchyRank: 0,
    joinedOnDay,
    alive: true,
  };
  // Drawn last, so giving a bird a bearing and three hidden numbers does not
  // renumber the id, genotype or lay offset every draw above already made. The
  // join day goes into the same call, so the week of watching cannot be dated
  // from a day the bird has not arrived on.
  chicken.personality = assignPersonality(rng, joinedOnDay);
  return chicken;
}

/** Default PR weather until the real forecast arrives (WEATHER-SYSTEM.md §1). */
export function defaultWeather(): WeatherState {
  return {
    condition: 'CLEAR',
    tempF: 84,
    humidity: 70,
    windMph: 8,
    gustMph: 12,
    isDay: true,
    sunriseHour: 6.2,
    sunsetHour: 18.8,
    stormAlert: 'none',
    source: 'simulated',
    fetchedAt: 0,
    locationLabel: 'Puerto Rico',
  };
}

/** The tutorial brooder the chicks arrive in, owned from the start. */
function starterStructures(rng: Rng): Structure[] {
  return [
    // REFOCUS §11b: "Start: coop included". The basic home is the one thing a
    // flock cannot start without, so it ships in the kit rather than sitting in
    // the store at 300 Eggits against an opening purse of 60.
    {
      id: newId('str', rng),
      itemId: 'coop_l1',
      state: initialStructureState(getItem('coop_l1')!),
    },
    {
      id: newId('str', rng),
      itemId: 'brooder_box',
      state: { lampSetting: 55, dirtiness: 0 },
    },
    {
      id: newId('str', rng),
      itemId: 'chick_waterer',
      state: { water: 100, waterPurity: 100 },
    },
    {
      id: newId('str', rng),
      itemId: 'chick_feeder',
      state: { feed: 100, feedType: 'starter' },
    },
    // Starter nest box so day-one eggs have a home. Without this the hens
    // laid but the player couldn't see them and breeding wasn't observable.
    {
      id: newId('str', rng),
      itemId: 'nesting_box',
      state: { dirtiness: 0, eggs: [] },
    },
  ];
}

export function createInitialState(
  seed = Date.now() >>> 0,
  housing: HousingType = 'backyard',
): GameState {
  const rng = new Rng(seed);
  const grid = housing === 'balcony' ? createGrid(4, 4, 'balcony_mat') : createGrid(6, 6);
  const flock: ChickenEntity[] = STARTER_BREEDS.map((breed, i) => {
    // Nube the Leghorn starts as an adult layer so day one pays out:
    // first egg in minutes, tutorial completable session 1.
    // Leghorn onset 19wk (133d); 140d guarantees laying age.
    if (i === 1) {
      const layer = createChick(rng, breed, STARTER_NAMES[i], { x: 2 + i, y: 3 }, 140, 'hen');
      layer.stage = 'adult';
      return layer;
    }
    return createChick(rng, breed, STARTER_NAMES[i], { x: 2 + i, y: 3 }, 14, 'hen');
  });
  // Starter rooster so breeding is reachable on day one.
  flock.push(createChick(rng, STARTER_ROOSTER_BREED, STARTER_ROOSTER_NAME, { x: 5, y: 3 }, 14, 'rooster'));

  const state: GameState = {
    schemaVersion: SCHEMA_VERSION,
    // Stamped at creation, not on load: there is no migration ladder any more,
    // so a save written without it would read as v0 and be discarded on the very
    // next boot.
    saveVersion: CURRENT_SAVE_VERSION,
    seed,
    lastSavedTimestamp: Date.now(),
    tickCount: 0,
    // Start at 7am — the morning chore hour, so day one begins with the loop.
    simHour: 7,
    // Real-time only (REFOCUS-2026-09.md §4). The sim runs while the player is
    // away, so the first egg still arrives without anyone watching the clock.
    timeMode: 'real',
    timeSpeed: DEFAULT_SPEED,
    housing,
    // REFOCUS §11b: the opening purse is the season's, not a constant. 60 is the
    // spec's figure and only balances because the coop is in the starter kit.
    eggits: seasonStartEggits(),
    grid,
    flock,
    structures: starterStructures(rng),
    inventory: { feed_starter: 2, wood_shavings: 1 },
    weather: defaultWeather(),
    neighbor: { annoyance: 0, smell: 0, noiseDb: 0, warningsIssued: 0, finesIssued: 0 },
    storm: { active: false, isDrill: false, completed: [] },
    progression: {
      badges: [],
      factsSeen: [],
      unlocks: [],
      tutorialStep: 0,
      tutorialComplete: false,
      certificateEarned: false,
      eggsCollected: 0,
      choreCompletionDays: [],
      choresDoneToday: [],
      currentDay: '',
      consecutiveCleanDays: 0,
      nightsWithoutPredator: 0,
      daysWithoutComplaint: 0,
      complainedToday: false,
      brooderOptimalHours: 0,
      brooderTotalHours: 0,
      breedQuizPassed: false,
      geneticsPredictionCorrect: false,
      careLog: [],
    },
    cosmetics: { owned: [] },
    settings: {
      // PR-first: Spanish if the browser says so, °F either way.
      locale: typeof navigator !== 'undefined' && navigator.language?.startsWith('es') ? 'es' : 'en',
      useCelsius: false,
      reducedMotion:
        typeof matchMedia !== 'undefined' &&
        matchMedia('(prefers-reduced-motion: reduce)').matches,
      soundEnabled: true,
      musicEnabled: true,
      showRealCosts: true,
      scienceToasts: true,
    },
    veteran: { enabled: false, unlocked: false, flockDaysSurvived: 0, bestScore: 0 },
    account: { userId: null, parentEmail: null, lastSyncedAt: null, redeemedCodes: [] },
  };
  // The starter equipment goes into the housing's first spots (REFOCUS §7).
  ensureSpots(state);
  return state;
}
